import { useMemo } from 'react'
import { CellContext } from '@tanstack/react-table'

import { BusStop, Log, LogLocationType, MandatoryLogInformation, MileageLossCode, RouteBusStops } from '@/types/logs'
import { parseLocationStopValue } from '@/modules/CombinedLogs/helpers/location'
import { getSelectedStops } from '@/modules/CombinedLogs/helpers/getSelectedStops'
import { MissingInfo } from './MissingInfo'

import styles from './Location.module.scss'

import BridgeIcon from '../../../assets/bridge.svg'

interface Props
    extends CellContext<
        Log,
        { locationTo: string; locationFrom: string; locationType: LogLocationType; direction: string }
    > {
    getRouteBusStops: (route?: string | undefined) => '' | RouteBusStops | undefined
    allStopsData: BusStop[]
}

export const Location = ({ getValue, row, getRouteBusStops, allStopsData }: Props) => {
    const { locationTo, locationFrom, locationType } = getValue()
    // const { getRouteBusStops, allStops: allStopsData } = useBusStops()
    const routeStops = getRouteBusStops(row.original.routes[0])

    const allStops = routeStops ? [...routeStops.outbound, ...routeStops.inbound] : allStopsData

    const locationFromName = useMemo(() => {
        return (
            locationFrom &&
            allStops.find((item) => {
                const { stopNumber, direction } = parseLocationStopValue(locationFrom)
                return item.stopNumber === stopNumber && item.direction === direction
            })?.stopName
        )
    }, [locationFrom, allStops])

    const locationToName = useMemo(() => {
        return (
            locationTo &&
            allStops.find((item) => {
                const { stopNumber, direction } = parseLocationStopValue(locationTo)
                return item.stopNumber === stopNumber && item.direction === direction
            })?.stopName
        )
    }, [locationTo, allStops])

    const isLowBridgeDetected = useMemo(() => {
        if (!locationFrom || !locationTo) {
            return false
        }

        const selectedStops = getSelectedStops(allStops, locationFrom, locationTo, locationType)

        if (selectedStops.length < 2) {
            return false
        }

        const from = allStops.find((item) => {
            const { stopNumber, direction } = parseLocationStopValue(locationFrom)
            return item.stopNumber === stopNumber && item.direction === direction
        })

        const to = allStops.find((item) => {
            const { stopNumber, direction } = parseLocationStopValue(locationTo)
            return item.stopNumber === stopNumber && item.direction === direction
        })

        const isAllStops = !from?.isCurtailmentStop || !to?.isCurtailmentStop

        return selectedStops.filter((stop) => isAllStops || stop.isCurtailmentStop).some((stop) => stop.isNearLowBridge)
    }, [locationFrom, locationTo, locationType, allStops])

    const isDefaultLocationOption = !(row.original.code && row.original.code in MileageLossCode)

    if (isDefaultLocationOption) {
        return locationFrom ? (
            <p>{locationFrom}</p>
        ) : (
            <MissingInfo log={row.original} label="location" field={MandatoryLogInformation.LocationFrom} />
        )
    }

    return (
        <div className={styles.root}>
            {isLowBridgeDetected && (
                <div className={styles.bridgeIconWrapper}>
                    <BridgeIcon />
                </div>
            )}
            <div className={styles.container}>
                <div className={styles.info}>
                    {locationFromName ? (
                        <>
                            <span className={styles.label}>From:</span>{' '}
                            <span className={styles.text}>{locationFromName}</span>
                        </>
                    ) : (
                        <MissingInfo log={row.original} label="from" field={MandatoryLogInformation.LocationFrom} />
                    )}
                </div>
                <div className={styles.info}>
                    {locationToName ? (
                        <>
                            <span className={styles.label}>To:</span>{' '}
                            <span className={styles.text}>{locationToName}</span>
                        </>
                    ) : (
                        <MissingInfo log={row.original} label="to" field={MandatoryLogInformation.LocationTo} />
                    )}
                </div>
            </div>
        </div>
    )
}
